import {
  GetApplicationResponse,
  GetDocumentUploadResponse,
  GetFormalOffersResponse,
  GetIwocapayBuyerPayLinkPackageResponse,
  GetIwocapayPaymentLaterResponse,
  GetIwocapaySellerConnectionResponse,
  GetIwocapaySellerOnboardingAnswersResponse,
  GetIwocapayShopifySellerShopResponse,
  GetLoanDocumentsRequiredIwocapayResponse,
  GetPayLinkByPayLinkIdResponse,
  GetPayLinkResponse,
  GetProductOffersResponse,
  GetSellerAgreementDocumentResponse,
  GetSellerHandleResponse,
  GetSellerIntegrationsRegisterInterestResponse,
  GetSellerOnboardingRequirementsResponse,
  GetSellerPricingResponse,
  GetSellerResponse,
  PostDocumentUploadResponse,
  PostFormalOffersRequestBody,
  PostFundSellerRequestResponse,
  PostIwocapayPaymentNowResponse,
  PostIwocapaySellerOnboardingAnswersRequestBody,
} from '@iwoca/lapi-client/edge';
import {
  GetFundingRequirementResponse,
  GetPaymentMethodResponse,
} from '@iwoca/lapi-client/v1';
import { waitFor } from '@testing-library/dom';
import fetchMock, {
  MockOptionsMethodGet,
  MockOptionsMethodPost,
} from 'fetch-mock';
import { v4 as uuidv4 } from 'uuid';

import {
  CustomerState,
  GetCustomerStateResponse,
  GetOnfidoCheckPayload,
  PricingType,
} from './edge';
import { ORDER_ID } from '../../Buyer/Orders/order.testHelpers';
import { expectToHaveBeenCalledWithRequestBody } from '../../Buyer/PayLinkLanding/GenericPayLinkLanding/GenericPayLinkLanding.testHelpers';
import { denormaliseDate } from '../../Buyer/Signup/stateNormalisation';
import { PAY_LINK_ID } from '../../Pages/Paylink.testHelpers';
import { EDGE_STATE_API_URL, STATE_KEY } from '../../Pages/state.testHelpers';
import { PackageLinksApiResponse } from '../../Seller/PayLinks/PackageLinkPage/PackageLinksPage.types';
import { TSellerProduct } from '../../Seller/PayLinks/utils/Paylinks.types';
import { expectFetchBody, waitForFetchBody } from '../../testingUtils';

export const FUNDING_REQUIREMENT_URL = `/api/lending/v1/funding_requirement/${STATE_KEY}/?requested_amount=1000&pay_link_id=${PAY_LINK_ID}`;

export function setupStateApiGetSuccess(
  data: GetCustomerStateResponse = { data: {} },
  options?: MockOptionsMethodGet,
) {
  fetchMock.get(
    `/api/lending/edge/state/${STATE_KEY}/`,
    {
      status: 200,
      body: {
        data: {
          state_key: STATE_KEY,
          ...data.data,
        },
      },
    },
    {
      overwriteRoutes: true,
      ...options,
    },
  );
}

export function setupGetState403(options?: MockOptionsMethodGet) {
  fetchMock.get(
    `/api/lending/edge/state/${STATE_KEY}/`,
    {
      status: 403,
      body: {
        errors: [
          {
            code: 'PermissionDenied',
            detail: 'You do not have permission to perform this action.',
          },
        ],
      },
    },
    options,
  );
}

export function setupStateApiPutSuccess({
  stateKey = STATE_KEY,
}: {
  stateKey?: string;
} = {}) {
  const url = `/api/lending/edge/state/${stateKey}/`;
  fetchMock.putOnce(url, {
    status: 202,
    body: {
      data: {
        version: uuidv4(),
      },
    },
  });

  const verifyStateApiPutCalled = async (expected: CustomerState) => {
    await waitForFetchBody({ data: expected }, url, 'put');
  };

  return { verifyStateApiPutCalled };
}

export function setupStateApiPostSuccess() {
  const url = `/api/lending/edge/state/`;
  fetchMock.postOnce(url, {
    status: 201,
    body: {
      data: {
        version: uuidv4(),
      },
    },
  });

  const verifyStateApiPostCalled = async (expected: CustomerState) => {
    await expectFetchBody({ data: expected }, url, 'post');
  };

  return { verifyStateApiPostCalled };
}

export const SELLER_NAME = 'test seller name';
export const SELLER_HANDLE = 'seller-handle';

export const setupGetSellerByHandleSuccess = ({
  handle = SELLER_HANDLE,
  data,
}: {
  handle?: string;
  data?: Partial<GetSellerHandleResponse>;
} = {}) => {
  const sellerByHandleResponse: GetSellerHandleResponse = {
    seller_name: SELLER_NAME,
    pricing: {
      promotions: ['first_month_interest_free'],
      representative_interest: 0.015,
      ...data?.pricing,
    },
    product_pricing: {
      THREE_MONTHS: {
        promotions: ['first_month_interest_free'],
        representative_interest: 0.015,
      },
      TWELVE_MONTHS: { promotions: [], representative_interest: 0 },
    },
    ...data,
  };

  fetchMock.getOnce(`/api/lending/edge/seller/handle/${handle}/`, {
    status: 200,
    body: sellerByHandleResponse,
  });
};

export const setupGetSellerByHandle404 = ({ handle }: { handle: string }) => {
  fetchMock.getOnce(`/api/lending/edge/seller/handle/${handle}/`, {
    status: 404,
  });
};

export const setupGetSellerByHandle500 = ({ handle }: { handle: string }) => {
  fetchMock.getOnce(`/api/lending/edge/seller/handle/${handle}/`, {
    status: 500,
  });
};

export const setupGetSellerByHandle400 = ({ handle }: { handle: string }) => {
  fetchMock.getOnce(`/api/lending/edge/seller/handle/${handle}/`, {
    status: 400,
    body: {
      errors: [{ code: 'BadRequest', detail: 'This seller is blocked.' }],
    },
  });
};

export const setupPostPayNowPaymentSuccess = ({
  handle,
}: {
  handle: string;
}) => {
  const url = `/api/lending/edge/iwocapay/payment/now/${handle}/`;

  const payNowPaymentResponse: PostIwocapayPaymentNowResponse = {
    data: {
      payment_provider_auth_uri: 'http://www.paymentProviderAuthUri.com',
    },
  };

  fetchMock.postOnce(url, {
    status: 200,
    body: payNowPaymentResponse,
  });
};

export const setupPostPayNowPayment500 = ({ handle }: { handle: string }) => {
  fetchMock.postOnce(`/api/lending/edge/iwocapay/payment/now/${handle}/`, {
    status: 500,
  });
};

export const setupPostPayLaterSuccess = ({
  stateKey,
}: {
  stateKey: string;
}) => {
  const url = `/api/lending/edge/iwocapay/payment/later/${stateKey}/`;

  fetchMock.postOnce(url, {
    status: 201,
    body: { data: { id: '123' } },
  });

  return {
    verifyPostPayLaterCalledWith: (expected: object) =>
      expectFetchBody(expected, url, 'post'),
  };
};

export const setupPostPayLater500 = ({ stateKey }: { stateKey: string }) => {
  const url = `/api/lending/edge/iwocapay/payment/later/${stateKey}/`;

  fetchMock.postOnce(url, {
    status: 500,
  });
};

export const setupGetPaylinksSuccess = (
  { body }: { body?: GetIwocapayPaymentLaterResponse } = {
    body: {
      data: [
        {
          amount: 150,
          created: '2022-10-14T14:54:28Z',
          funded_at: '2022-10-14T14:54:28Z',
          id: '7a63af27-ea3d-42e8-bf2f-d29c854988cf',
          is_funded: false,
          order: {
            order_id: '',
          },
          pricing: {
            representative_interest: 0,
            promotions: [],
          },
          reference: 'fridge',
          seller_name: 'Smart Fridge',
          source: 'iwocaPay',
        },
      ],
    },
  },
) => {
  const PAYLINKS_URL = `/api/lending/edge/iwocapay/payment/later/${STATE_KEY}/`;

  fetchMock.getOnce(PAYLINKS_URL, {
    status: 200,
    body,
  });
};

export const setupGetPaylinksDuplicatesExistSuccess = (
  { body }: { body?: GetIwocapayPaymentLaterResponse } = {
    body: {
      data: [
        {
          amount: 1000,
          created: '2022-10-14T14:54:28Z',
          funded_at: '2022-10-14T14:54:28Z',
          id: '7a63af27-ea3d-42e8-bf2f-d29c854988cf',
          is_funded: false,
          order: {
            order_id: '',
          },
          pricing: {
            representative_interest: 0,
            promotions: [],
          },
          reference: 'fridge',
          seller_name: 'Smart Fridge',
          source: 'iwocaPay',
        },
      ],
    },
  },
) => {
  const PAYLINKS_URL = `/api/lending/edge/iwocapay/payment/later/${STATE_KEY}/`;

  fetchMock.getOnce(PAYLINKS_URL, {
    status: 200,
    body,
  });
};

export const setupGetPaylinksMultipleExistSuccess = (
  { body }: { body?: GetIwocapayPaymentLaterResponse } = {
    body: {
      data: [
        {
          amount: 2000.0,
          created: '2022-10-14T12:00:28Z',
          funded_at: '2022-10-14T12:00:28Z',
          id: '7a63af27-ea3d-42e8-bf2f-d29c854988cf',
          is_funded: false,
          order: {
            order_id: '',
          },
          pricing: {
            representative_interest: 0,
            promotions: [],
          },
          reference: 'watch',
          seller_name: 'Smart Tech',
          source: 'iwocaPay',
        },
        {
          amount: 500.0,
          created: '2022-01-14T07:54:28Z',
          funded_at: '2022-01-14T07:54:28Z',
          id: '7a63af27-ea3d-42e8-bf2f-d29c854988cf',
          is_funded: false,
          order: {
            order_id: '',
          },
          pricing: {
            representative_interest: 0,
            promotions: [],
          },
          reference: 'tv',
          seller_name: 'Smart Tech',
          source: 'iwocaPay',
        },
        {
          amount: 999.99,
          created: '2022-08-14T10:30:28Z',
          funded_at: '2022-08-14T10:30:28Z',
          id: '7a63af27-ea3d-42e8-bf2f-d29c854988cf',
          is_funded: false,
          order: {
            order_id: '',
          },
          pricing: {
            representative_interest: 0,
            promotions: [],
          },
          reference: 'fridge',
          seller_name: 'Smart Tech',
          source: 'iwocaPay',
        },
      ],
    },
  },
) => {
  const PAYLINKS_URL = `/api/lending/edge/iwocapay/payment/later/${STATE_KEY}/`;

  fetchMock.getOnce(PAYLINKS_URL, {
    status: 200,
    body,
  });
};

export const setupGetSellerSuccess = ({
  stateKey = STATE_KEY,
  handle = 'shipping-folks',
  options,
}: {
  stateKey?: string;
  handle?: string;
  options?: MockOptionsMethodGet;
} = {}) => {
  const url = `/api/lending/edge/seller/${stateKey}/`;

  const sellerResponse: GetSellerResponse = {
    data: {
      handle,
    },
  };

  fetchMock.getOnce(
    url,
    {
      status: 200,
      body: sellerResponse,
    },
    options,
  );
};

export const setupPostSellerSuccess = ({
  stateKey = STATE_KEY,
  options = {},
}: {
  stateKey?: string;
  options?: MockOptionsMethodPost;
} = {}) => {
  const url = `/api/lending/edge/seller/${stateKey}/`;

  fetchMock.postOnce(
    url,
    {
      status: 200,
      body: {},
    },
    options,
  );

  const verifyPostSellerCalled = async (expected: boolean) => {
    return await waitFor(() =>
      expect(fetchMock.called(url, 'post')).toBe(expected),
    );
  };

  return { verifyPostSellerCalled };
};

export const setupGetSeller404 = ({
  stateKey = STATE_KEY,
  options,
}: { stateKey?: string; options?: MockOptionsMethodGet } = {}) => {
  const url = `/api/lending/edge/seller/${stateKey}/`;

  fetchMock.getOnce(
    url,
    {
      status: 404,
      body: {
        data: {
          errors: [
            {
              code: 'NotFound',
              detail: 'No "state key" found matching that query.',
            },
          ],
        },
      },
    },
    options,
  );
};

export const setupGetSeller400 = ({
  stateKey = STATE_KEY,
  options,
}: { stateKey?: string; options?: MockOptionsMethodGet } = {}) => {
  const url = `/api/lending/edge/seller/${stateKey}/`;

  fetchMock.getOnce(
    url,
    {
      status: 400,
      body: {
        data: {
          errors: [
            {
              code: 'BadRequest',
              detail: 'This seller is blocked.',
            },
          ],
        },
      },
    },
    options,
  );
};

export function setupGetReferralCode(referralCode: string) {
  const url = `/api/lending/edge/iwocapay/referral/${STATE_KEY}/`;

  fetchMock.getOnce(url, {
    data: {
      referral_code: referralCode,
    },
  });
}

export function setupGetReferralsCount(referralCount: number) {
  const url = `/api/lending/edge/iwocapay/referrals/${STATE_KEY}/`;

  fetchMock.getOnce(url, {
    data: {
      referral_count: referralCount,
    },
  });
}

export function setupGetReferralsCount500() {
  const url = `/api/lending/edge/iwocapay/referrals/${STATE_KEY}/`;

  fetchMock.getOnce(url, {
    status: 500,
  });
}

export const setupGetReferrerSuccess = ({
  referralCode,
  referrerName,
}: {
  referralCode: string;
  referrerName: string;
}) => {
  const url = `/api/lending/edge/iwocapay/referrer/${referralCode}/`;

  fetchMock.getOnce(url, {
    status: 200,
    body: {
      data: {
        referrer_name: referrerName,
      },
    },
  });
};

export const setupPostOnfidoStart201 = ({ body }: { body: object }) => {
  const url = `/api/lending/edge/onfido/start/${STATE_KEY}/`;

  fetchMock.postOnce(url, {
    status: 201,
    body,
  });

  const verifyPostOnfidoStart = async (expected: object) => {
    await expectFetchBody(expected, url);
  };

  return { verifyPostOnfidoStart };
};

export const setupPostOnfidoStart400 = () => {
  const url = `/api/lending/edge/onfido/start/${STATE_KEY}/`;

  fetchMock.postOnce(url, {
    status: 400,
    body: {},
  });
};

export const setupGetOnfidoCheckSuccess = ({
  body,
}: {
  body: GetOnfidoCheckPayload;
}) => {
  const personUid = '123-123-123-123';
  const url = `/api/lending/edge/onfido/check/${STATE_KEY}/${personUid}/`;

  fetchMock.get(url, {
    status: 200,
    body: body,
  });

  const verifyGetOnfidoCheck = async (expected: unknown) => {
    await expectFetchBody(expected, url, 'get');
  };

  return { verifyGetOnfidoCheck };
};

export const setupGetPackageLinksSuccess = ({
  body,
}: {
  body: PackageLinksApiResponse;
}) => {
  const url = `/api/lending/edge/iwocapay/pay_link_package/${STATE_KEY}/`;

  fetchMock.getOnce(
    url,
    {
      status: 200,
      body,
    },
    {
      overwriteRoutes: true,
    },
  );
};

export const PAY_LINK_PACKAGE_ID = 'c6fa4384-60b4-424f-8c0c-1fb1d902dcf1';
export const setupGetPayLinkPackageSuccess = (
  data: Partial<GetIwocapayBuyerPayLinkPackageResponse['data']>,
) => {
  const url = `/api/lending/edge/iwocapay/buyer/pay_link_package/${PAY_LINK_PACKAGE_ID}/`;

  const body: GetIwocapayBuyerPayLinkPackageResponse = {
    data: {
      id: PAY_LINK_PACKAGE_ID,
      name: 'Ragnarok Package',
      reference: 'Fishing in midgard',
      amount: 10000,
      pricing: {
        promotions: ['first_month_interest_free'],
        representative_interest: 0.015,
        ...data.pricing,
      },
      ...data,
    },
  };

  fetchMock.getOnce(
    url,
    {
      status: 200,
      body,
    },
    {
      overwriteRoutes: true,
    },
  );
};

export const setupPostSellerOnboardingAnswers = ({
  stateKey = STATE_KEY,
  data,
}: {
  stateKey?: string;
  data: PostIwocapaySellerOnboardingAnswersRequestBody;
}) => {
  const url = `/api/lending/edge/iwocapay/seller_onboarding_answers/${stateKey}/`;
  fetchMock.postOnce(url, {
    status: 200,
    body: { data },
  });

  return {
    verifyPostSellerOnboardingAnswersWith: (
      expected: PostIwocapaySellerOnboardingAnswersRequestBody,
    ) => expectFetchBody(expected!, url, 'post'),
  };
};

const DEFAULT_ONBOARDING_ANSWERS: GetIwocapaySellerOnboardingAnswersResponse['data'] =
  {
    annual_turnover: 'prefer_not_to_say',
    typical_customer: 'consumers',
    reason_for_use: 'avoid_paying_card_fees',
    invoices_per_month: '30+',
    payment_collection: 'e_commerce_checkout',
  };

export const setupGetSellerOnboardingAnswersSuccess = ({
  stateKey = STATE_KEY,
  data = DEFAULT_ONBOARDING_ANSWERS,
}: {
  stateKey?: string;
  data?: GetIwocapaySellerOnboardingAnswersResponse['data'];
} = {}) => {
  const url = `/api/lending/edge/iwocapay/seller_onboarding_answers/${stateKey}/`;

  fetchMock.getOnce(url, {
    status: 200,
    body: {
      data,
    },
  });
};

export const setupGetSellerOnboardingAnswers404 = ({
  stateKey = STATE_KEY,
}: {
  stateKey?: string;
} = {}) => {
  const url = `/api/lending/edge/iwocapay/seller_onboarding_answers/${stateKey}/`;

  fetchMock.getOnce(url, {
    status: 404,
  });
};

export const setupPostUploadBankDocumentsSuccess = ({
  body,
}: {
  body: PostDocumentUploadResponse;
}) => {
  const url = `/api/lending/edge/document_upload/${STATE_KEY}/`;

  fetchMock.post(
    url,
    {
      status: 200,
      body,
    },
    {
      overwriteRoutes: true,
    },
  );

  const verifyUploadBankDocuments = async () => {
    await waitFor(() => expect(fetchMock.called(url, 'post')).toBe(true));
  };

  return { verifyUploadBankDocuments };
};

export const setupGetSellerPricingSuccess = ({
  stateKey = STATE_KEY,
  activePricing = 'SELLER_PAYS_BUYER_FREE',
  activePricing_12m,
}: {
  stateKey?: string;
  activePricing?: PricingType;
  activePricing_12m?: PricingType;
} = {}) => {
  const url = `/api/lending/edge/seller/${stateKey}/pricing/`;
  const body: GetSellerPricingResponse = {
    data: {
      active: activePricing,
      options: [
        {
          type: 'SELLER_PAYS_BUYER_ONE_MONTH_FREE',
          buyer: {
            representative_interest: 0.015,
            promotions: ['first_month_interest_free'],
          },
          seller: { fee: 0.03 },
        },

        {
          type: 'SELLER_PAYS_BUYER_FREE',
          buyer: {
            representative_interest: 0,
            promotions: [],
          },
          seller: { fee: 0.03 },
        },
        {
          type: 'SELLER_FREE_BUYER_ONE_MONTH_FREE',
          buyer: {
            representative_interest: 0.015,
            promotions: ['first_month_interest_free'],
          },
          seller: { fee: 0 },
        },
        {
          type: 'SELLER_FREE_BUYER_PAYS',
          buyer: {
            representative_interest: 0.015,
            promotions: [],
          },
          seller: { fee: 0 },
        },
      ],
      active_product_pricing: {
        THREE_MONTHS: { type: activePricing },
        ...(activePricing_12m && {
          TWELVE_MONTHS: { type: activePricing_12m },
        }),
      },
      product_pricing_options: {
        THREE_MONTHS: [
          {
            type: 'SELLER_PAYS_BUYER_ONE_MONTH_FREE',
            buyer: {
              representative_interest: 0.015,
              promotions: ['first_month_interest_free'],
            },
            seller: { fee: 0.03 },
          },

          {
            type: 'SELLER_PAYS_BUYER_FREE',
            buyer: {
              representative_interest: 0,
              promotions: [],
            },
            seller: { fee: 0.03 },
          },
          {
            type: 'SELLER_FREE_BUYER_ONE_MONTH_FREE',
            buyer: {
              representative_interest: 0.015,
              promotions: ['first_month_interest_free'],
            },
            seller: { fee: 0 },
          },
          {
            type: 'SELLER_FREE_BUYER_PAYS',
            buyer: {
              representative_interest: 0.015,
              promotions: [],
            },
            seller: { fee: 0 },
          },
        ],
        ...(activePricing_12m && {
          TWELVE_MONTHS: [
            {
              type: 'SELLER_PAYS_BUYER_FREE',
              buyer: {
                representative_interest: 0,
                promotions: [],
              },
              seller: { fee: 0.17 },
            },
            {
              type: 'SELLER_FREE_BUYER_PAYS',
              buyer: {
                representative_interest: 0.0333,
                promotions: [],
              },
              seller: { fee: 0 },
            },
          ],
        }),
      },
    },
  };

  fetchMock.getOnce(
    url,
    {
      status: 200,
      body,
    },
    {
      overwriteRoutes: true,
    },
  );
};

export const setupPutSellerPricingSuccess = ({
  stateKey,
  activePricing,
}: {
  stateKey: string;
  activePricing: PricingType;
}) => {
  const url = `/api/lending/edge/seller/${stateKey}/pricing/`;

  fetchMock.putOnce(url, {
    status: 202,
    body: {
      data: {
        active: activePricing,
      },
    },
  });
};

export const setupPutSellerPricing500 = ({
  stateKey,
}: {
  stateKey: string;
}) => {
  const url = `/api/lending/edge/seller/${stateKey}/pricing/`;

  fetchMock.putOnce(url, {
    status: 500,
  });
};

export const DEFAULT_REQUIREMENTS: GetSellerOnboardingRequirementsResponse['data'] =
  [
    {
      requirement: 'seller_agreement_contract',
      resolution_methods: [
        {
          errors: [],
          status: 'completed',
          type: 'seller_agreement_signed',
        },
      ],
      status: 'completed',
    },
    {
      requirement: 'completed_risk_assessment',
      resolution_methods: [
        {
          errors: [],
          status: 'completed',
          type: 'manual_risk_assessment',
        },
      ],
      status: 'completed',
    },
    {
      requirement: 'verified_bank_details_limited_company',
      resolution_methods: [
        {
          errors: ['Missing Experian Bank Wizard data'],
          status: 'error',
          type: 'experian_bank_wizard',
        },
        {
          errors: [],
          status: 'completed',
          type: 'passthrough',
        },
      ],
      status: 'completed',
    },
    {
      requirement: 'completed_business_profile',
      resolution_methods: [
        {
          errors: ['Missing answers'],
          status: 'error',
          type: 'onboarding_questions_answered',
        },
        {
          errors: ['Awaiting seller agreement to be signed'],
          status: 'required',
          type: 'seller_agreement_signed',
        },
        {
          errors: [],
          status: 'completed',
          type: 'passthrough',
        },
      ],
      status: 'completed',
    },
    {
      requirement: 'completed_directors_profile',
      resolution_methods: [
        {
          errors: ['Missing person details'],
          status: 'error',
          type: 'directors_profile',
        },
        {
          errors: [],
          status: 'completed',
          type: 'passthrough',
        },
      ],
      status: 'completed',
    },
  ];

export const setupSellerOnboardingRequirementsSuccess = ({
  stateKey = STATE_KEY,
  requirements = DEFAULT_REQUIREMENTS,
  options,
}: {
  stateKey?: string;
  requirements?: GetSellerOnboardingRequirementsResponse['data'];
  options?: MockOptionsMethodGet;
} = {}) => {
  const url = `/api/lending/edge/seller_onboarding_requirements/${stateKey}/`;

  fetchMock.getOnce(
    url,
    {
      status: 200,
      body: {
        data: requirements,
      },
    },
    options,
  );
};

export const setupSellerOnboardingRequirementsError = ({
  stateKey = STATE_KEY,
  options,
}: {
  stateKey?: string;
  options?: MockOptionsMethodGet;
} = {}) => {
  const url = `/api/lending/edge/seller_onboarding_requirements/${stateKey}/`;

  fetchMock.getOnce(
    url,
    {
      status: 403,
      body: {},
    },
    options,
  );
};

type SellerAgreement = GetSellerAgreementDocumentResponse['data'];
export const setupSellerAgreementSuccess = ({
  stateKey = STATE_KEY,
  data,
}: {
  stateKey?: string;
  data: SellerAgreement;
}) => {
  const url = `/api/lending/edge/seller_agreement_document/${stateKey}/`;

  fetchMock.getOnce(url, {
    status: 200,
    body: {
      data,
    },
  });
};

export const setupPutSignSellerAgreementDocument = ({
  stateKey = STATE_KEY,
  documentId,
}: {
  stateKey?: string;
  documentId: number;
}) => {
  const url = `/api/lending/edge/seller_agreement_document/${stateKey}/sign/${documentId}/`;

  fetchMock.putOnce(url, {
    status: 202,
  });

  const verifyPutSignSellerAgreeemntDocumentCalled = async () => {
    await waitFor(() => expect(fetchMock.called(url, 'put')).toBe(true));
  };

  return { verifyPutSignSellerAgreeemntDocumentCalled };
};

export const setupPayLinkApiPutSuccess = (stateKey: string = STATE_KEY) => {
  const url = `/api/lending/edge/seller/${stateKey}/pay_link/`;

  fetchMock.putOnce(url, {
    status: 202,
    body: { success: true },
  });
  const verifyPutPayLinkApiCalled = async () => {
    await waitFor(() => expect(fetchMock.called(url, 'put')).toBe(true));
  };

  return { verifyPutPayLinkApiCalled };
};

export const setupGetDocumentUploadSuccess = ({
  body,
}: {
  body: GetDocumentUploadResponse;
}) => {
  const url = `/api/lending/edge/document_upload/${STATE_KEY}/`;

  fetchMock.get(
    url,
    {
      status: 200,
      body,
    },
    {
      overwriteRoutes: true,
    },
  );
};

export function setupPayLinkGetSuccess({
  url,
  body,
  options,
}: {
  body: GetPayLinkResponse;
  url: string;
  options?: MockOptionsMethodGet;
}) {
  fetchMock.getOnce(
    url,
    {
      status: 200,
      body: body,
    },
    options,
  );
}

type RequestedProductType = NonNullable<
  NonNullable<GetApplicationResponse['data']>['requested_products']
>[number]['product_type'];

type ApplicationStatus = NonNullable<GetApplicationResponse['data']>['status'];

export function setupGetApplicationSuccess(
  {
    status,
    productType,
  }: {
    status: ApplicationStatus;
    productType: RequestedProductType;
  } = { status: 'open', productType: 'iwocapay' },
) {
  const APPLICATION_URL = `/api/lending/edge/application/${STATE_KEY}/`;

  fetchMock.getOnce(
    APPLICATION_URL,
    {
      status: 200,
      body: {
        data: {
          requested_products: [
            {
              product_type: productType,
              amount: '300',
              urgency: 'future_use',
              duration: {
                unit: 'months',
                amount: 3,
              },
            },
          ],
          status,
        },
      },
    },
    { overwriteRoutes: true },
  );
}

export function setupGetApplication12MonthsSuccess() {
  const APPLICATION_URL = `/api/lending/edge/application/${STATE_KEY}/`;

  fetchMock.getOnce(
    APPLICATION_URL,
    {
      status: 200,
      body: {
        data: {
          requested_products: [
            {
              product_type: 'iwocapay',
              amount: '300',
              urgency: 'future_use',
              duration: {
                unit: 'days',
                amount: 365,
              },
            },
          ],
          status: 'open',
        },
      },
    },
    { overwriteRoutes: true },
  );
}

export function setupPostApplicationDecline() {
  const APPLICATION_DECLINE_URL = `/api/lending/edge/application/${STATE_KEY}/decline/`;

  fetchMock.post(APPLICATION_DECLINE_URL, {
    status: 202,
  });
}

export function setupGetProductOffersSuccess({
  body,
}: {
  body: GetProductOffersResponse;
}) {
  const PRODUCT_OFFERS_URL = `/api/lending/edge/product_offers/${STATE_KEY}/`;

  fetchMock.get(PRODUCT_OFFERS_URL, {
    status: 200,
    body,
  });
}

export function setupPostFormalOffersApiSuccess({
  body,
}: {
  body: PostFormalOffersRequestBody;
}) {
  const FORMAL_OFFERS_URL = `/api/lending/edge/formal_offers/${STATE_KEY}/`;

  fetchMock.post(FORMAL_OFFERS_URL, {
    status: 200,
    body,
  });
}

export const setupSingleImmediatePaymentSuccess = (options = {}) => {
  fetchMock.postOnce(
    '/api/lending/edge/single_immediate_payment/',
    {
      status: 201,
      body: {
        data: {
          payment_provider_auth_uri: 'http://www.payment_provider_list.com',
        },
      },
    },
    options,
  );
};

export const setUpSingleImmediatePayment400 = () => {
  fetchMock.postOnce('/api/lending/edge/single_immediate_payment/', {
    status: 400,
    body: {
      errors: [],
    },
  });
};

export function setupPutState() {
  fetchMock.put(`${EDGE_STATE_API_URL}${STATE_KEY}/`, {
    data: {
      state_key: STATE_KEY,
    },
  });
}

export function setupPutStateOnce() {
  fetchMock.putOnce(`${EDGE_STATE_API_URL}${STATE_KEY}/`, {
    data: {
      state_key: STATE_KEY,
    },
  });
}

export function setupPutApplicationOnce() {
  fetchMock.putOnce(`/api/lending/edge/application/${STATE_KEY}/`, {
    code: 200,
  });
}

export const setupGetFormalOffersSuccess = (
  { body }: { body?: GetFormalOffersResponse } = {
    body: { data: { formal_offers: [] } },
  },
) => {
  const FORMAL_OFFERS_URL = `/api/lending/edge/formal_offers/${STATE_KEY}/`;

  fetchMock.getOnce(FORMAL_OFFERS_URL, {
    status: 200,
    body,
  });
};

export function setupGetPayLinkNoPayLinkSuccess() {
  const PAYLINK_URL = `/api/lending/edge/pay_link/`;

  fetchMock.getOnce(PAYLINK_URL, {
    status: 200,
    body: {},
  });
}

function setupGetPayLinkSuccess(body: GetPayLinkByPayLinkIdResponse) {
  const PAYLINK_URL = `/api/lending/edge/pay_link/${PAY_LINK_ID}/`;

  fetchMock.getOnce(PAYLINK_URL, {
    status: 200,
    body,
  });
}

export const setupGetPayLinkNoInterestSuccess = (
  partialMockPayLinkResponse: Partial<GetPayLinkByPayLinkIdResponse> = {},
) => {
  const responseBody: GetPayLinkByPayLinkIdResponse = {
    id: PAY_LINK_ID,
    amount: 1000,
    pricing: {
      representative_interest: 0,
      promotions: [],
    },
    created: new Date().toDateString(),
    order: {
      order_id: ORDER_ID,
    },
    reference: 'Service Mjolnir',
    seller_name: 'Thor Constructions',
    seller_handle: SELLER_HANDLE,
    product_pricing: {
      THREE_MONTHS: {
        representative_interest: 0,
        promotions: [],
      },
      TWELVE_MONTHS: {
        representative_interest: 0,
        promotions: [],
      },
    },
    allowed_payment_terms: ['PAY_LATER', 'PAY_NOW'],
    source: 'iwocaPay',
    ...partialMockPayLinkResponse,
  };

  setupGetPayLinkSuccess(responseBody);
};

export const setupGetPayLinkWithInterestSuccess = (
  partialMockPayLinkResponse: Partial<GetPayLinkByPayLinkIdResponse> = {},
) => {
  const responseBody: GetPayLinkByPayLinkIdResponse = {
    id: PAY_LINK_ID,
    amount: 1000,
    pricing: {
      representative_interest: 0.015,
      promotions: [],
    },
    created: new Date().toDateString(),
    order: {
      order_id: ORDER_ID,
    },
    reference: 'Service Mjolnir',
    seller_name: 'Thor Constructions',
    seller_handle: SELLER_HANDLE,
    product_pricing: {
      THREE_MONTHS: {
        representative_interest: 0.025,
        promotions: [],
      },
      TWELVE_MONTHS: {
        representative_interest: 0.015,
        promotions: [],
      },
    },
    allowed_payment_terms: ['PAY_LATER', 'PAY_NOW'],
    source: 'iwocaPay',
    ...partialMockPayLinkResponse,
  };

  setupGetPayLinkSuccess(responseBody);
};

export const setupGetFundingRequirementsIncompleted = ({
  optionalPaymentMethod = false,
  url = FUNDING_REQUIREMENT_URL,
}: {
  optionalPaymentMethod?: boolean;
  url?: string;
} = {}) => {
  fetchMock.getOnce(
    url,
    {
      status: 200,
      body: {
        data: [
          {
            category: 'payments',
            status: 'required',
            resolution_methods: [
              {
                type: 'add_default_payment_method',
                items: [],
                errors: [],
                status: 'required',
              },
            ],
            recommended_resolution_method: 'add_default_payment_method',
            comments: '',
            is_optional: optionalPaymentMethod,
            type: 'active_payment_method_requirement',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'contractual',
            status: 'required',
            resolution_methods: [
              {
                type: 'sign_documents',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method: 'sign_documents',
            comments: '',
            is_optional: false,
            type: 'signed_documents_requirement',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'credit_rules',
            status: 'completed',
            resolution_methods: [
              {
                type: 'complete_signup',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method: 'complete_signup',
            comments: '',
            is_optional: false,
            type: 'incomplete_signup_requirement',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'credit_rules',
            status: 'completed',
            resolution_methods: [
              {
                type: 'get_valid_approval',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method: 'get_valid_approval',
            comments: '',
            is_optional: false,
            type: 'valid_approval_requirement',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'credit_rules',
            status: 'completed',
            resolution_methods: [
              {
                type: 'request_appropriate_loan_amount',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method: 'request_appropriate_loan_amount',
            comments: '',
            is_optional: false,
            type: 'loan_size_requirement',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'id_checks',
            status: 'required',
            resolution_methods: [
              {
                type: 'bde_fraud_check',
                items: [],
                errors: [],
                status: 'required',
              },
              {
                type: 'onfido_check',
                items: [
                  {
                    name: 'f1aef82e-863c-45eb-ac0a-2b193f1c987a',
                    status: 'required',
                  },
                ],
                errors: [],
                status: 'required',
              },
              {
                type: 'equifax_eiv_check',
                items: [],
                errors: [],
                status: 'required',
              },
            ],
            recommended_resolution_method: 'onfido_check',
            comments: '',
            is_optional: false,
            type: 'fraud_check_requirement',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'enhanced_security_check',
            status: 'required',
            recommended_resolution_method: 'contact_iwoca',
          },
          {
            category: 'id_checks',
            status: 'required',
            resolution_methods: [
              {
                type: 'major_shareholders_check',
                items: [],
                errors: [
                  {
                    code: '146',
                    detail:
                      'Check Shareholder Information. Missing MSH: CYNTHIA MORGAN',
                  },
                  {
                    code: '147',
                    detail:
                      'Verify Shareholdings. Data suggests Ervin Norton not MSH',
                  },
                ],
                status: 'required',
              },
            ],
            recommended_resolution_method: 'major_shareholders_check',
            comments: '',
            is_optional: false,
            type: 'associated_people_check',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'aml_checks',
            status: 'error',
            resolution_methods: [
              {
                type: 'run_aml_check',
                items: [],
                errors: [
                  {
                    code: '127',
                    detail:
                      'error performing aml check: Ervin Norton failed to match one of the directors from Experian BI data',
                  },
                  {
                    code: '127',
                    detail:
                      'error performing aml check: Ervin Norton failed AML check for callml',
                  },
                ],
                status: 'error',
              },
            ],
            recommended_resolution_method: 'run_aml_check',
            comments: '',
            is_optional: false,
            type: 'anti_money_laundering_check',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'payments',
            status: 'completed',
            resolution_methods: [
              {
                type: 'check_iwocapay_pay_link',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method: 'check_iwocapay_pay_link',
            comments: '',
            is_optional: false,
            type: 'valid_iwocapay_pay_link',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'payments',
            status: 'completed',
            resolution_methods: [
              {
                type: 'check_product_is_iwocapay',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method: 'check_product_is_iwocapay',
            comments: '',
            is_optional: false,
            type: 'check_product_is_iwocapay_to_fund_pay_link',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'contractual',
            status: 'completed',
            resolution_methods: [
              {
                type: 'check_mla_covers_post_funding_balance',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method:
              'check_mla_covers_post_funding_balance',
            comments: '',
            is_optional: false,
            type: 'check_mla_covers_post_funding_balance',
            quantifier: 1,
            quantifier_unit: null,
          },
        ],
      },
    },
    { overwriteRoutes: true },
  );
};

const payLinkRequirementCompleted = [
  {
    category: 'payments',
    status: 'completed',
    resolution_methods: [
      {
        type: 'check_iwocapay_pay_link',
        items: [],
        errors: [],
        status: 'completed',
      },
    ],
    recommended_resolution_method: 'check_iwocapay_pay_link',
    comments: '',
    is_optional: false,
    type: 'valid_iwocapay_pay_link',
    quantifier: 1,
    quantifier_unit: null,
  },
];

const payLinkRequirementIncompleted = [
  {
    category: 'payments',
    status: 'error',
    resolution_methods: [
      {
        type: 'check_iwocapay_pay_link',
        items: [],
        errors: [],
        status: 'error',
      },
    ],
    recommended_resolution_method: 'check_iwocapay_pay_link',
    comments: '',
    is_optional: false,
    type: 'valid_iwocapay_pay_link',
    quantifier: 1,
    quantifier_unit: null,
  },
];

export const setupGetFundingRequirementsCompleted = ({
  url = FUNDING_REQUIREMENT_URL,
  hasPayLink = true,
} = {}) => {
  fetchMock.getOnce(
    url,
    {
      status: 200,
      body: {
        data: [
          {
            category: 'payments',
            status: 'required',
            resolution_methods: [
              {
                type: 'add_default_payment_method',
                items: [],
                errors: [],
                status: 'required',
              },
            ],
            recommended_resolution_method: 'add_default_payment_method',
            comments: '',
            is_optional: true,
            type: 'active_payment_method_requirement',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'contractual',
            status: 'completed',
            resolution_methods: [
              {
                type: 'sign_documents',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method: 'sign_documents',
            comments: '',
            is_optional: false,
            type: 'signed_documents_requirement',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'credit_rules',
            status: 'completed',
            resolution_methods: [
              {
                type: 'complete_signup',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method: 'complete_signup',
            comments: '',
            is_optional: false,
            type: 'incomplete_signup_requirement',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'credit_rules',
            status: 'completed',
            resolution_methods: [
              {
                type: 'get_valid_approval',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method: 'get_valid_approval',
            comments: '',
            is_optional: false,
            type: 'valid_approval_requirement',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'credit_rules',
            status: 'completed',
            resolution_methods: [
              {
                type: 'request_appropriate_loan_amount',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method: 'request_appropriate_loan_amount',
            comments: '',
            is_optional: false,
            type: 'loan_size_requirement',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'id_checks',
            status: 'completed',
            resolution_methods: [
              {
                type: 'bde_fraud_check',
                items: [],
                errors: [],
                status: 'required',
              },
              {
                type: 'onfido_check',
                items: [
                  {
                    name: 'f1aef82e-863c-45eb-ac0a-2b193f1c987a',
                    status: 'completed',
                  },
                ],
                errors: [],
                status: 'completed',
              },
              {
                type: 'equifax_eiv_check',
                items: [],
                errors: [],
                status: 'required',
              },
            ],
            recommended_resolution_method: 'onfido_check',
            comments: '',
            is_optional: false,
            type: 'fraud_check_requirement',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'enhanced_security_check',
            status: 'completed',
            recommended_resolution_method: 'contact_iwoca',
          },
          {
            category: 'id_checks',
            status: 'completed',
            resolution_methods: [
              {
                type: 'major_shareholders_check',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method: 'major_shareholders_check',
            comments: '',
            is_optional: false,
            type: 'associated_people_check',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'aml_checks',
            status: 'completed',
            resolution_methods: [
              {
                type: 'run_aml_check',
                items: [],
                errors: [],
                status: 'error',
              },
            ],
            recommended_resolution_method: 'run_aml_check',
            comments: '',
            is_optional: false,
            type: 'anti_money_laundering_check',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'payments',
            status: 'completed',
            resolution_methods: [
              {
                type: 'check_product_is_iwocapay',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method: 'check_product_is_iwocapay',
            comments: '',
            is_optional: false,
            type: 'check_product_is_iwocapay_to_fund_pay_link',
            quantifier: 1,
            quantifier_unit: null,
          },
          {
            category: 'contractual',
            status: 'completed',
            resolution_methods: [
              {
                type: 'check_mla_covers_post_funding_balance',
                items: [],
                errors: [],
                status: 'completed',
              },
            ],
            recommended_resolution_method:
              'check_mla_covers_post_funding_balance',
            comments: '',
            is_optional: false,
            type: 'check_mla_covers_post_funding_balance',
            quantifier: 1,
            quantifier_unit: null,
          },
          ...(hasPayLink
            ? payLinkRequirementCompleted
            : payLinkRequirementIncompleted),
        ],
      },
    },
    { overwriteRoutes: true },
  );
};

export const FORMAL_OFFER_ID = 'bbbbxxxx-bbbb-cccc-dddd-eeeeyyyyzzzz';

export const setupGetAccountsReturnsNone = () => {
  const ACCOUNTS_URL = `/api/lending/edge/accounts/${STATE_KEY}/`;
  fetchMock.getOnce(ACCOUNTS_URL, {
    status: 200,
    body: { data: {} },
  });
};

export const setupGetAccountsSuccessNonIwocaPay = () => {
  const ACCOUNTS_URL = `/api/lending/edge/accounts/${STATE_KEY}/`;
  fetchMock.getOnce(ACCOUNTS_URL, {
    status: 200,
    body: {
      data: {
        accounts: [
          {
            product_type: 'credit_facility',
            formal_offers: [
              {
                offer_id: FORMAL_OFFER_ID,
                max_amount: 15000,
                max_duration: {
                  unit: 'days',
                  amount: 91,
                },
                timeout: '2022-10-14',
                is_timed_out: false,
                available_credit: 15000,
                refinance_amount: 0,
                refinance_type: 'no_refinancing',
                available_cash: 15000,
                interest_rates: [
                  {
                    as_of: 0,
                    rate: 0.025,
                  },
                ],
                product_type: 'credit_facility',
                bip_upfront_fee: 0,
                withdrawal_fee_percentage: 0,
              },
            ],
          },
        ],
      },
    },
  });
};

export const setupGetAccountsSuccess = () => {
  const ACCOUNTS_URL = `/api/lending/edge/accounts/${STATE_KEY}/`;

  fetchMock.getOnce(ACCOUNTS_URL, {
    status: 200,
    body: {
      data: {
        accounts: [
          {
            product_type: 'iwocapay',
            formal_offers: [
              {
                offer_id: FORMAL_OFFER_ID,
                max_amount: 15000,
                max_duration: {
                  unit: 'days',
                  amount: 91,
                },
                timeout: '2022-10-14',
                is_timed_out: false,
                available_credit: 15000,
                refinance_amount: 0,
                refinance_type: 'no_refinancing',
                available_cash: 15000,
                interest_rates: [
                  {
                    as_of: 0,
                    rate: 0.015,
                  },
                ],
                product_type: 'iwocapay',
                bip_upfront_fee: 0,
                withdrawal_fee_percentage: 0,
              },
            ],
          },
        ],
      },
    },
  });
};

export const setupGetAccountsPartialApproval = () => {
  const ACCOUNTS_URL = `/api/lending/edge/accounts/${STATE_KEY}/`;

  fetchMock.getOnce(ACCOUNTS_URL, {
    status: 200,
    body: {
      data: {
        accounts: [
          {
            product_type: 'iwocapay',
            formal_offers: [
              {
                offer_id: FORMAL_OFFER_ID,
                max_amount: 15000,
                max_duration: {
                  unit: 'days',
                  amount: 91,
                },
                timeout: '2022-10-14',
                is_timed_out: false,
                available_credit: 15000,
                refinance_amount: 0,
                refinance_type: 'no_refinancing',
                available_cash: 100,
                interest_rates: [
                  {
                    as_of: 0,
                    rate: 0.015,
                  },
                ],
                product_type: 'iwocapay',
                bip_upfront_fee: 0,
                withdrawal_fee_percentage: 0,
              },
            ],
          },
        ],
      },
    },
  });
};

export const setupGetDrawdownRepaymentScheduleSuccess = ({
  totalInterest = 0,
}: Partial<{
  totalInterest: number;
}> = {}) => {
  const DRAWDOWN_REPAYMENT_SCHEDULE_URL = `/api/lending/edge/draw_down_repayment_schedule/${STATE_KEY}/iwocapay/${FORMAL_OFFER_ID}/?pay_link_id=${PAY_LINK_ID}`;
  fetchMock.getOnce(DRAWDOWN_REPAYMENT_SCHEDULE_URL, {
    status: 200,
    body: {
      data: {
        summary: {
          total_number_of_repayments: 3,
          business_days_only: true,
          total_credit_amount: 999.99,
          total_interest: totalInterest,
          total_fees: 0,
          total_payable: 999.99,
          apr: 0,
          annual_interest_rate: {
            rate_type: 'fixed',
          },
          simplified_schedule: {
            equal_repayments: {
              amount: 333.33,
              interval: 'Monthly',
              day_of_month: 14,
              final_standard_repayment_date: '2022-12-14',
              first_standard_repayment_date: '2022-10-14',
            },
          },
          is_equal_repayment: true,
          equal_repayment_amount: 333.33,
        },
        schedule: [
          {
            date: '2022-10-14',
            total_fees: 0,
            total: 333.33,
            interest: 0,
            principal: 333.33,
          },
          {
            date: '2022-11-14',
            total_fees: 0,
            total: 333.33,
            interest: 0,
            principal: 333.33,
          },
          {
            date: '2022-12-14',
            total_fees: 0,
            total: 333.33,
            interest: 0,
            principal: 333.33,
          },
        ],
      },
    },
  });
};

export const setupGetRequiredDocumentsSuccess = (
  response: GetLoanDocumentsRequiredIwocapayResponse,
) => {
  const REQUIRED_DOCUMENTS_URL = `/api/lending/edge/loan_documents/${STATE_KEY}/required/iwocapay/${FORMAL_OFFER_ID}/?pay_link_id=${PAY_LINK_ID}`;
  fetchMock.get(
    REQUIRED_DOCUMENTS_URL,
    {
      status: 200,
      body: response,
    },
    { overwriteRoutes: true },
  );
};

export const PERSONAL_GUARANTEE_DOCUMENT_ID =
  'd18e9fdf-2eb0-45ff-a780-bc89cf6259f8';
export const MASTER_LOAN_AGREEMENT_ID = '6e6381e4-fb77-48cd-aee9-f0ca4ca502ee';

export const PERSONAL_GUARANTEE_UNSIGNED_URL = `/api/lending/edge/loan_documents/${STATE_KEY}/unsigned/${PERSONAL_GUARANTEE_DOCUMENT_ID}/`;
export const PERSONAL_GUARANTEE_SIGNED_URL = `/api/lending/edge/loan_documents/${STATE_KEY}/signed/${PERSONAL_GUARANTEE_DOCUMENT_ID}/`;

export const MASTER_AGREEMENT_UNSIGNED_URL = `/api/lending/edge/loan_documents/${STATE_KEY}/unsigned/${MASTER_LOAN_AGREEMENT_ID}/`;
export const MASTER_AGREEMENT_SIGNED_URL = `/api/lending/edge/loan_documents/${STATE_KEY}/signed/${MASTER_LOAN_AGREEMENT_ID}/`;

export const setupGetRequiredDocumentsUnsignedSuccess = () => {
  return setupGetRequiredDocumentsSuccess({
    data: {
      required_loan_documents: [
        {
          document_id: MASTER_LOAN_AGREEMENT_ID,
          type: 'master_loan_agreement',
          label: 'Master Agreement',
          title: 'Credit Agreement',
          description:
            'This sets out the terms that will apply to all your future transactions with iwoca',
          signature_text:
            'Signing of behalf of Ervin Norton: Only sign this agreement if you want to be legally bound by its terms.',
          agreed: false,
          required_method: 'electronic_signature',
          required_person_to_sign_name: 'Ervin Norton',
          required_person_to_sign_uid: 'f1aef82e-863c-45eb-ac0a-2b193f1c987a',
          downloads: {
            unsigned: MASTER_AGREEMENT_UNSIGNED_URL,
          },
        },
        {
          document_id: PERSONAL_GUARANTEE_DOCUMENT_ID,
          type: 'guarantee',
          label: 'Guarantee',
          title: 'Your Personal Guarantee and Indemnity',
          signature_text:
            "Signing of behalf of Ervin Norton: You are guaranteeing that Thor Constructions will make the required repayments on their iwoca facility. If the business fails to pay, you'll be personally liable for the repayments",
          agreed: false,
          required_method: 'electronic_signature',
          required_person_to_sign_name: 'Ervin Norton',
          required_person_to_sign_uid: '9b1cb114-530a-45d6-bdc5-30e965efc1c1',
          downloads: {
            unsigned: PERSONAL_GUARANTEE_UNSIGNED_URL,
          },
        },
      ],
    },
  });
};

export const setupGetRequiredDocumentsMLASignedSuccess = () => {
  return setupGetRequiredDocumentsSuccess({
    data: {
      required_loan_documents: [
        {
          document_id: MASTER_LOAN_AGREEMENT_ID,
          type: 'master_loan_agreement',
          label: 'Master Agreement',
          title: 'Credit Agreement',
          description:
            'This sets out the terms that will apply to all your future transactions with iwoca',
          signature_text:
            'Signing of behalf of Ervin Norton: Only sign this agreement if you want to be legally bound by its terms.',
          agreed: true,
          required_method: 'electronic_signature',
          required_person_to_sign_name: 'Ervin Norton',
          required_person_to_sign_uid: 'f1aef82e-863c-45eb-ac0a-2b193f1c987a',
          downloads: {
            unsigned: MASTER_AGREEMENT_UNSIGNED_URL,
            signed: MASTER_AGREEMENT_SIGNED_URL,
          },
        },
      ],
    },
  });
};

export const setupGetRequiredDocumentsPersonalGuaranteeSignedSuccess = () => {
  return setupGetRequiredDocumentsSuccess({
    data: {
      required_loan_documents: [
        {
          document_id: PERSONAL_GUARANTEE_DOCUMENT_ID,
          type: 'guarantee',
          label: 'Guarantee',
          title: 'Your Personal Guarantee and Indemnity',
          signature_text:
            "Signing of behalf of Ervin Norton: You are guaranteeing that Thor Constructions will make the required repayments on their iwoca facility. If the business fails to pay, you'll be personally liable for the repayments",
          agreed: true,
          required_method: 'electronic_signature',
          required_person_to_sign_name: 'Ervin Norton',
          required_person_to_sign_uid: '9b1cb114-530a-45d6-bdc5-30e965efc1c1',
          downloads: {
            unsigned: PERSONAL_GUARANTEE_UNSIGNED_URL,
            signed: PERSONAL_GUARANTEE_SIGNED_URL,
          },
        },
      ],
    },
  });
};

export const CREDIT_FACILITY_DOCUMENT_URL = `/api/lending/v1/credit_facility_customer_document/${STATE_KEY}/`;
export const setupPutDocumentSuccess = (
  documentId = '6e6381e4-fb77-48cd-aee9-f0ca4ca502ee',
) => {
  fetchMock.putOnce(CREDIT_FACILITY_DOCUMENT_URL, {});

  const verifyDocumentSigned = async () => {
    await waitFor(() =>
      expect(fetchMock.called(CREDIT_FACILITY_DOCUMENT_URL)).toBe(true),
    );

    expectToHaveBeenCalledWithRequestBody(CREDIT_FACILITY_DOCUMENT_URL, {
      data: {
        customer_documents: [
          {
            document_id: documentId,
            agreed: true,
            agreement_date: denormaliseDate(
              new Date().toLocaleDateString('en-GB'),
            ),
          },
        ],
      },
    });
  };

  return { verifyDocumentSigned };
};

export const setupGetFundingRequirements = ({
  body,
  options,
}: {
  body?: GetFundingRequirementResponse;
  options?: MockOptionsMethodGet;
} = {}) => {
  fetchMock.getOnce(
    FUNDING_REQUIREMENT_URL,
    {
      status: 200,
      body,
    },
    options,
  );
};

export const setupPostRepaymentMethodSuccess = () => {
  const REPAYMENT_METHOD_URL = `/api/lending/v1/repayment/${STATE_KEY}/payment_method/`;

  fetchMock.postOnce(REPAYMENT_METHOD_URL, {
    status: 200,
    body: {
      data: {
        accepted_card_types: ['debit', 'prepaid'],
        secret: 'SECRET',
      },
    },
  });
};

export const setupPostRepaymentMethod500 = () => {
  const REPAYMENT_METHOD_URL = `/api/lending/v1/repayment/${STATE_KEY}/payment_method/`;

  fetchMock.postOnce(REPAYMENT_METHOD_URL, {
    status: 500,
  });
};

export const setupGetPaymentMethodSuccess = (
  paymentMethod?: Partial<GetPaymentMethodResponse['data'][number]>,
) => {
  const PAYMENT_METHOD_URL = `/api/lending/v1/payment_method/${STATE_KEY}/`;

  fetchMock.getOnce(PAYMENT_METHOD_URL, {
    status: 200,
    body: {
      data: [
        {
          payment_method_id: '2c9458bf-f549-48ac-835d-59080a302451',
          registered_at: '2022-09-23T10:48:16Z',
          payment_method_type: 'card',
          token_details: {
            provider: 'stripe',
          },
          roles: {
            funding: 'disabled',
            repayment: 'primary',
          },
          brand: 'visa',
          expires: {
            year: 2034,
            month: 12,
          },
          last_digits: '5556',
          issuer: 'NETWORK ONLY',
          ...paymentMethod,
        },
      ],
    },
  });
};

export const PERSONAL_GUARANTEE_UNSIGNED_CONTENT =
  'Personal Guarantee Document for Ervin';

export const PERSONAL_GUARANTEE_SIGNED_CONTENT =
  '[SIGNED] Personal Guarantee Document for Ervin';

export function setupGetPersonalGuaraneteeUnsignedSuccess() {
  fetchMock.get(PERSONAL_GUARANTEE_UNSIGNED_URL, {
    status: 200,
    body: `<html>${PERSONAL_GUARANTEE_UNSIGNED_CONTENT}</html>`,
  });
}

export function setupGetMasterAgreementUnsignedSuccess() {
  fetchMock.get(MASTER_AGREEMENT_UNSIGNED_URL, {
    status: 200,
    body: `<html>${PERSONAL_GUARANTEE_UNSIGNED_CONTENT}</html>`,
  });
}

export function setupGetPersonalGuaraneteeSignedSuccess() {
  fetchMock.get(PERSONAL_GUARANTEE_SIGNED_URL, {
    status: 200,
    body: `<html>${PERSONAL_GUARANTEE_SIGNED_CONTENT}</html>`,
  });
}

export const setupFundSeller = ({
  payLinkId,
  success,
  //@ts-expect-error
  response = { redirect_url: null },
}: {
  payLinkId: string;
  success: boolean;
  response?: PostFundSellerRequestResponse;
}) => {
  const FUND_SELLER_URL = `/api/lending/edge/fund_seller_request/${STATE_KEY}/${payLinkId}/`;
  const status = success ? 201 : 400;

  fetchMock.postOnce(FUND_SELLER_URL, {
    status,
    body: response,
  });

  const verifyFundSellerCalledOnce = async () => {
    await waitFor(() =>
      expect(fetchMock.calls(FUND_SELLER_URL)).toHaveLength(1),
    );
  };

  return { verifyFundSellerCalledOnce };
};

export const setupGetIwocapaySellerConnectionSuccess = ({
  response,
}: {
  response: GetIwocapaySellerConnectionResponse;
}) => {
  fetchMock.get(
    `/api/lending/edge/iwocapay/seller/${STATE_KEY}/connection/`,
    {
      status: 200,
      body: response,
    },
    {
      overwriteRoutes: true,
    },
  );
};

export const setupPostQuickbooksDisconnectSuccess = () => {
  fetchMock.postOnce(
    `/api/lending/edge/quickbooks/disconnect/${STATE_KEY}/`,
    {
      status: 200,
    },
    {
      overwriteRoutes: true,
    },
  );
};

export const setupPostIntegrationIntentSuccess = () => {
  fetchMock.postOnce(
    `/api/lending/edge/seller/${STATE_KEY}/integrations/register_interest/`,
    {
      status: 201,
      body: {},
    },
  );
};

export const setupGetIntegrationsInterestSucess = ({
  response,
}: {
  response: GetSellerIntegrationsRegisterInterestResponse;
}) => {
  fetchMock.get(
    `/api/lending/edge/seller/${STATE_KEY}/integrations/register_interest/`,
    {
      status: 200,
      body: response,
    },
    {
      overwriteRoutes: true,
    },
  );
};

export function setupSuccessPasswordlessEmail(
  email: string,
  redirect_to: string,
) {
  fetchMock.post('/api/lending/edge/passwordless/email/', {
    status: 200,
  });

  const verifyPasswordlessEndpointCalled = async () => {
    await waitFor(() =>
      fetchMock.called('/api/lending/edge/passwordless/email/', {
        body: {
          data: {
            email,
            redirect_to,
          },
        },
      }),
    );
  };

  return {
    verifyPasswordlessEndpointCalled,
  };
}

export function setup503PasswordlessEmail() {
  fetchMock.post('/api/lending/edge/passwordless/email/', {
    status: 503,
    body: {
      errors: [
        {
          code: 'PermissionDenied',
          detail: 'You do not have permission to perform this action.',
        },
      ],
    },
  });
}

export const setupPutCancelCapturedPayLink = ({
  stateKey = STATE_KEY,
  payLinkId = PAY_LINK_ID,
}: { stateKey?: string; payLinkId?: string } = {}) => {
  const url = `/api/lending/edge/iwocapay/seller/${stateKey}/payment/captured/${payLinkId}/cancel/`;

  fetchMock.putOnce(url, {
    status: 202,
    body: {},
  });

  const verifyPutCancelCapturedPayLinkCalled = async (expected: boolean) => {
    return await waitFor(() =>
      expect(fetchMock.called(url, 'put')).toBe(expected),
    );
  };

  return { verifyPutCancelCapturedPayLinkCalled };
};

export const setupPutCancelCapturedPayLink400Error = ({
  stateKey = STATE_KEY,
  payLinkId = PAY_LINK_ID,
}: { stateKey?: string; payLinkId?: string } = {}) => {
  const url = `/api/lending/edge/iwocapay/seller/${stateKey}/payment/captured/${payLinkId}/cancel/`;

  fetchMock.putOnce(url, {
    status: 400,
    body: {
      errors: [
        {
          code: 'BadRequest',
          detail: 'This captured payment has been cancelled already.',
        },
      ],
    },
  });

  const verifyPutCancelCapturedPayLinkCalled = async (expected: boolean) => {
    return await waitFor(() =>
      expect(fetchMock.called(url, 'put')).toBe(expected),
    );
  };

  return { verifyPutCancelCapturedPayLinkCalled };
};

export function setupAccountLogoutApiPost() {
  const logoutUrl = '/api/lending/edge/logout/';
  fetchMock.post(logoutUrl, { status: 201 }, { overwriteRoutes: true });

  const verifyAccountLogoutCalled = async () => {
    await waitFor(() => expect(fetchMock.called(logoutUrl)).toBe(true));
  };

  return { verifyAccountLogoutCalled };
}

export function createCustomerStateData({
  emailAddress,
}: {
  emailAddress: string;
}) {
  return {
    state_key: STATE_KEY,
    application: {
      people: [
        {
          emails: [{ email: emailAddress, uid: 'mock-uuid' }],
          uid: 'mock-uuid',
        },
      ],
    },
  };
}

export function getEmailAvailabilityUrl(email: string) {
  return `/api/lending/v1/email_availability/?email=${encodeURIComponent(
    email,
  )}`;
}

export function setupEmailAvailabilityApiGet({
  email,
  available,
}: {
  email: string;
  available: boolean;
}) {
  fetchMock.get(
    getEmailAvailabilityUrl(email),
    {
      status: 200,
      body: {
        data: {
          available,
        },
      },
    },
    { overwriteRoutes: true },
  );
  const verifyEmailAvailabilityApiGetCalled = async () => {
    await waitFor(() =>
      expect(fetchMock.called(getEmailAvailabilityUrl(email))).toBe(true),
    );
  };

  return { verifyEmailAvailabilityApiGetCalled };
}

export function createState(
  emailAddress: string,
  marketingOptIn: boolean,
  paylinkId?: string,
) {
  return {
    application: {
      people: [
        {
          last_name: '',
          uid: uuidv4(),
          roles: ['applicant'],
          first_name: '',
          emails: [
            {
              email: emailAddress,
              marketing_opt_in: {
                agreed: marketingOptIn,
              },
              uid: uuidv4(),
              type: 'primary',
            },
          ],
        },
      ],
    },
    ui: paylinkId
      ? {
          iwocapay_paylink_id: paylinkId,
        }
      : {},
  };
}

export const FAKE_SELLER_STATE_KEY = 'aaaaxxxx-bbbb-cccc-dddd-eeeeyyyyzzz4';

const spendingLimitRequestBody = {
  uuid: 'aaaaxxxx-bbbb-cccc-dddd-eeeeyyyyzzz1',
  application_uuid: 'aaaaxxxx-bbbb-cccc-dddd-eeeeyyyyzzz2',
  seller_uuid: 'aaaaxxxx-bbbb-cccc-dddd-eeeeyyyyzzz3',
  seller_state_key: FAKE_SELLER_STATE_KEY,
  seller_handle: 'fake_seller_handle',
  redirect_url: null,
};

export function setupGetSpendingLimitRequestSuccess({
  stateKey = STATE_KEY,
  isBuyerPays = true,
}: {
  stateKey?: string;
  isBuyerPays?: boolean;
} = {}) {
  const SPENDING_LIMIT_REQUEST_URL = `/api/lending/edge/iwocapay/buyer/${stateKey}/spending_limit_request/`;
  const pricingType = isBuyerPays
    ? 'SELLER_FREE_BUYER_PAYS'
    : 'SELLER_PAYS_BUYER_FREE';

  fetchMock.get(SPENDING_LIMIT_REQUEST_URL, {
    status: 200,
    body: {
      data: {
        seller_product_pricing_type_3m: pricingType,
        ...spendingLimitRequestBody,
      },
    },
  });
}

export function setupGetSellerEnabledProducts({
  stateKey = STATE_KEY,
  payIn12Enabled = false,
}: {
  stateKey?: string;
  payIn12Enabled?: boolean;
}) {
  const url = `/api/lending/edge/seller/${stateKey}/enabled_products/`;
  const enabled_products = payIn12Enabled
    ? ['THREE_MONTHS', 'TWELVE_MONTHS']
    : ['THREE_MONTHS'];

  fetchMock.get(
    url,
    {
      status: 200,
      body: {
        data: {
          enabled_products: enabled_products,
        },
      },
    },
    { overwriteRoutes: true },
  );
}
export const setupPutSellerEnabledProducts = ({
  stateKey = STATE_KEY,
  enabled_products = ['THREE_MONTHS'],
}: {
  stateKey?: string;
  enabled_products?: TSellerProduct[];
}) => {
  const url = `/api/lending/edge/seller/${stateKey}/enabled_products/`;

  fetchMock.putOnce(url, {
    status: 200,
    body: {
      data: {
        enabled_products: enabled_products,
      },
    },
  });
};

export const setupGetShopifyShop = ({
  stateKey = STATE_KEY,
  response = { data: { shop_url: 'test_shop.myshopify.com' } },
}: {
  stateKey?: string;
  response?: GetIwocapayShopifySellerShopResponse;
}) => {
  fetchMock.get(
    `/api/lending/edge/iwocapay/shopify/seller/${stateKey}/shop/`,
    {
      status: 200,
      body: response,
    },
    {
      overwriteRoutes: true,
    },
  );
};
